import React, {
  FunctionComponent,
  useState,
  useRef,
  useEffect,
  FocusEvent,
  ChangeEvent,
  useMemo,
} from 'react';
import { FormCheck, FormControl, Overlay, Popover } from 'react-bootstrap';

type Props = {
  content: Record<string, string>;
  checked: string[];
  // onChange: (e: string[]) => void;
  onFocus: (e: FocusEvent<HTMLElement>) => void;
  onBlur: (e: FocusEvent<HTMLElement>) => void;
  onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
  deserialize?: (v: string) => string[];
  serialize?: (v: string[]) => string;
  sort?: (v: string[]) => string[];
  selectShow?: 'all' | 'key' | 'value';
  disableCheckAllSelection?: boolean;
};
export const DropdownCheckList: FunctionComponent<Props> = (
  {
    onChange,
    onBlur,
    onFocus,
    content,
    checked,
    deserialize = (v) => v.split(''),
    serialize = (v) => v.join(''),
    sort = (v) => v.sort(),
    selectShow = 'all',
    disableCheckAllSelection = false,
  } //   ...props
) => {
  const [show, setShow] = useState(false);
  const containerRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fadeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [checkedAll, setCheckedAll] = useState(false);
  const contentArray = useMemo(
    () =>
      Object.entries(content).map(([k, v]) => ({
        key: k,
        value: v,
      })),
    [content]
  );

  function checkboxChange(key: string, c: boolean) {
    if (inputRef.current) {
      const checkedKeys = deserialize(inputRef.current.value);
      const keySet = new Set(checkedKeys.filter((v) => v !== ''));
      if (c) {
        keySet.add(key);
      } else if (!c) {
        keySet.delete(key);
      }
      const newDatas = sort([...keySet.values()]);
      const serialized = serialize(newDatas);

      if (keySet.size === contentArray.length) {
        setCheckedAll(true);
      } else {
        setCheckedAll(false);
      }

      inputRef.current.value = serialized;
      const e = new Event('change', { bubbles: true });
      inputRef.current.dispatchEvent(e);
      onChange(e as any);
    }
  }

  function showOverlay() {
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    setShow(true);
  }

  function hideOverlay() {
    if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    fadeTimerRef.current = setTimeout(() => {
      setShow(false);
    }, 300);
  }

  const display = (key: string, value: string) => {
    switch (selectShow) {
      case 'key':
        return key;
      case 'value':
        return value;
      case 'all':
      default:
        return `${key} - ${value}`;
    }
  };

  const checkboxList = sort(Object.keys(content)).map((k) => {
    const v = content[k];
    const id = `dropdown-check-${k}`;

    return (
      <div
        key={k}
        onMouseDown={(e) => e.preventDefault()}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <FormCheck
          id={id}
          type="checkbox"
          name={k}
          checked={checked?.includes(k)}
          onChange={(e) => checkboxChange(k, e.target.checked)}
          style={{ margin: 0 }}
        />
        <label
          htmlFor={id}
          style={{
            cursor: 'pointer',
            userSelect: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {display(k, v)}
        </label>
      </div>
    );
  });

  if (!disableCheckAllSelection) {
    checkboxList.unshift(
      <div
        key="check-all"
        onMouseDown={(e) => e.preventDefault()}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        <FormCheck
          id="dropdown-check-all"
          type="checkbox"
          name="check-all"
          checked={checked?.length === Object.keys(content).length}
          onChange={(e) => {
            if (e.target.checked) {
              const newDatas = sort(Object.keys(content));
              const serialized = serialize(newDatas);
              if (inputRef.current) inputRef.current.value = serialized;
            } else {
              if (inputRef.current) inputRef.current.value = '';
            }
            const changeEvent = new Event('change', { bubbles: true });
            if (inputRef.current) inputRef.current.dispatchEvent(changeEvent);
            onChange(changeEvent as any);
          }}
          style={{ margin: 0 }}
        />
        <label
          htmlFor="dropdown-check-all"
          style={{
            cursor: 'pointer',
            userSelect: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          全選
        </label>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div ref={containerRef}>
        <FormControl
          type="text"
          ref={inputRef}
          value={serialize(checked)}
          onChange={(e) => {
            // setInputDatas(e.target.value);
            onChange(e);
          }}
          onFocus={(e: any) => {
            showOverlay();
            onFocus(e);
          }}
          onBlur={(e: any) => {
            hideOverlay();
            onBlur(e);
          }}
        />
        <Overlay
          container={containerRef}
          target={containerRef.current}
          show={show}
          placement="left-start"
          popperConfig={{ strategy: 'fixed' }}
        >
          <Popover id={`input-dropdown-popover`}>
            <Popover.Content
              style={{ minWidth: '12.5rem' }}
              onBlur={() => hideOverlay()}
              onFocus={() => showOverlay()}
            >
              {checkboxList}
            </Popover.Content>
          </Popover>
        </Overlay>
      </div>
    </React.Fragment>
  );
};
